import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Descriptions, Row, Col, Button, Alert, Tabs, Divider } from 'antd';
import { EditOutlined, FilePdfOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import useCustomerStore from '../../store/customerStore';
import { UserRole } from '../../models/users';
import ModalUtil from '../../store/core/modalUtil';
import CustomerFormModal from '../../components/Customers/CustomerFormModal';
import CustomerServicePrices from '../../components/Customers/CustomerServicePrices';
import './CustomerDetails.css';
import { useAuthStore } from '../../store/authUserStore';
import DataTable, { DataTableColumn } from '../../components/DataTable/DataTable';
import useEventStore from '../../store/eventStore';
import useStandStore from '../../store/standStore';
import useOrderStore from '../../store/orderStore';
import StandTypeSelectModal from '../../components/StandTypeSelectModal';

const CustomerDetails: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const { customerStands, fetchStandsByCustomerId, selectedItem, setSelectedItem, fetchItemById, fetchCustomerByUserId } = useCustomerStore();
  const { selectedCustomerOrders, fetchOrdersByCustomerId } = useOrderStore();
  const { currentUser } = useAuthStore();
  const { t } = useTranslation();
  const { selectedItem: customer } = useCustomerStore();

  const { customerEvents, fetchEventsByCustomerId, downloadInvoiceByCustomerId } = useEventStore();
  const { standsByEvent, fetchStandsByEventIds } = useStandStore();
  const { ordersByStand, fetchOrdersByStandIds } = useOrderStore();

  const [isOnline, setIsOnline] = React.useState(navigator.onLine);
  const [invoiceModal, setInvoiceModal] = React.useState({ open: false, loading: false });
  const [invoiceEvent, setInvoiceEvent] = React.useState<any>(null);

  React.useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!customer?.id) return;
    fetchEventsByCustomerId(customer.id);
  }, [customer?.id, fetchEventsByCustomerId]);

  useEffect(() => {
    if (!customerEvents?.length) return;
    const eventIds = customerEvents.map(e => e.id);
    fetchStandsByEventIds(eventIds);
  }, [customerEvents, fetchStandsByEventIds]);

  useEffect(() => {
    if (!standsByEvent) return;
    const allStands = Object.values(standsByEvent).flat();
    const standIds = allStands.map(s => s.id);
    fetchOrdersByStandIds(standIds);
  }, [standsByEvent, fetchOrdersByStandIds]);

  useEffect(() => {
    if (!customerId) return;
    fetchItemById(customerId);
  }, [customerId, fetchItemById]);

  useEffect(() => {
    if (!selectedItem || selectedItem.id !== customerId) {
      if (customerId) {
        fetchItemById(customerId!);
      } else if (!customerId && !selectedItem) {
        fetchCustomerByUserId(currentUser?.id!)
      }
    }
  }, [customerId, selectedItem, fetchItemById, fetchCustomerByUserId]);

  useEffect(() => {
    if (selectedItem) {
      customerId && fetchStandsByCustomerId(selectedItem.id!);
      fetchOrdersByCustomerId(selectedItem.id!);
    }
  }, [selectedItem, fetchStandsByCustomerId, fetchOrdersByCustomerId]);

  const orderColumns: DataTableColumn<any>[] = [
    { header: t('order.number'), accessor: 'orderNumber' },
    { header: t('general.date'), accessor: 'date', type: 'date' },
    {
      header: t('stand.deliveryNote'),
      accessor: '',
      renderCell: (order: any) => (
        <Button
          type="primary"
          size="small"
          onClick={async (e) => {
            e.stopPropagation();
            await useOrderStore.getState().downloadPDFByBillType(order.id, 'deliverynote');
          }}
        >
          {t('general.download')}
        </Button>
      ),
    },
    {
      header: t('stand.redemptionSlip'),
      accessor: '',
      renderCell: (order: any) => (
        <Button
          type="primary"
          size="small"
          onClick={async (e) => {
            e.stopPropagation();
            await useOrderStore.getState().downloadPDFByBillType(order.id, 'redemptionslip');
          }}
        >
          {t('general.download')}
        </Button>
      ),
    },
  ];

  const standColumns: DataTableColumn<any>[] = [
    { header: t('order.standName'), accessor: 'name' },
    {
      header: t('checkList.signature'),
      accessor: 'signature',
      renderCell: (stand: any) =>
        stand.signature ? (
          <Button
            size="small"
            onClick={e => {
              e.stopPropagation();
              ModalUtil.openModal(
                <div style={{ textAlign: 'center', padding: 16 }}>
                  <div style={{ marginBottom: 8 }}>{t('checkList.signature') || 'Signature'}</div>
                  <img
                    src={stand.signature}
                    alt="Signature"
                    style={{ maxWidth: 300, maxHeight: 120, border: '1px solid #ccc', borderRadius: 4 }}
                  />
                  <div style={{ marginTop: 8, color: '#888' }}>
                    {stand.name ? `${t('order.standName')}: ${stand.name}` : ''}
                  </div>
                </div>,
                t('checkList.signature') || 'Signature',
                "550px"
              );
            }}
          >
            {t('checkList.signature')}
          </Button>
        ) : (
          <span style={{ color: '#aaa' }}>{t('checkList.signature')}</span>
        ),
    },
  ];

  const eventColumns: DataTableColumn<any>[] = [
    { header: t('events.name'), accessor: 'name' },
    { header: t('events.startDate'), accessor: 'startDate', type: 'date' },
    { header: t('events.endDate'), accessor: 'endDate', type: 'date' },
    {
      header: t('events.downloadInvoice'),
      accessor: '',
      renderCell: (event: any) => (
        <Button
          type="primary"
          size="small"
          icon={<FilePdfOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            setInvoiceEvent(event);
            setInvoiceModal({ open: true, loading: false });
          }}
        >
          {t('general.download')}
        </Button>
      ),
    },
  ];

  const expandedStandRow = (stand: any) => (
    <DataTable
      columns={orderColumns}
      data={ordersByStand[stand.id] || []}
      enableFrontendFiltering={false}
      hidePagination
    />
  );

  const expandedEventRow = (event: any) => (
    <DataTable
      columns={standColumns}
      data={standsByEvent[event.id] || []}
      expandedRowRender={expandedStandRow}
      enableFrontendFiltering={false}
      hidePagination
    />
  );

  const handleEditCustomer = () => {
    setSelectedItem(customer);
    ModalUtil.openModal(
      <CustomerFormModal initialValues={customer} />,
      t('customer.edit'),
      "60%"
    );
  };

  if (!isOnline) {
    return (
      <div style={{ padding: 32, textAlign: 'center' }}>
        <Alert
          message={t('general.onlyOnline') || 'This screen is not available in offline mode.'}
          type="warning"
          showIcon
        />
      </div>
    );
  }

  return (
    <div data-tour="customer-details-page" style={{ padding: '0px 20px' }}>
      <StandTypeSelectModal
        open={invoiceModal.open}
        loading={invoiceModal.loading}
        onCancel={() => setInvoiceModal({ open: false, loading: false })}
        onConfirm={async (standType) => {
          setInvoiceModal(m => ({ ...m, loading: true }));
          await downloadInvoiceByCustomerId(selectedItem!, invoiceEvent?.id!, standType);
          setInvoiceModal({ open: false, loading: false });
        }}
      />
      <Card
        data-tour="customer-details-card"
        title={customer?.companyName}
        style={{ marginBottom: '20px' }}
        extra={
          currentUser?.role === UserRole.ADMIN && (
            <Button
              icon={<EditOutlined />}
              type="primary"
              onClick={handleEditCustomer}
            >
              {t('general.edit')}
            </Button>
          )
        }
      >
        <Tabs
          items={[
            {
              key: 'general',
              label: <span data-tour="customer-tab-general">{t('customer.general') || 'General'}</span>,
              children: (
                <>
                  <Row gutter={24} className='tablet'>
                    <Col span={24}>
                      <Descriptions bordered column={1}>
                        <Descriptions.Item label={t('customer.username')}>{selectedItem?.username || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('customer.firstName')}>{selectedItem?.firstName || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('customer.lastName')}>{selectedItem?.lastName || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('customer.companyName')}>{selectedItem?.companyName || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('customer.phone')}>{selectedItem?.phone || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('customer.email')}>{selectedItem?.email || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('customer.street')}>{selectedItem?.address?.street || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('customer.city')}>{selectedItem?.address?.city || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('customer.state')}>{selectedItem?.address?.state || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('customer.postalCode')}>{selectedItem?.address?.postalCode || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('customer.country')}>{selectedItem?.address?.country?.name || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('customer.taxId')}>{selectedItem?.taxId || '-'}</Descriptions.Item>
                      </Descriptions>
                    </Col>
                  </Row>

                  <Row gutter={16} className='desktop'>
                    <Col span={12}>
                      <Descriptions bordered column={1}>
                        <Descriptions.Item label={t('customer.username')}>{selectedItem?.username || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('customer.firstName')}>{selectedItem?.firstName || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('customer.lastName')}>{selectedItem?.lastName || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('customer.companyName')}>{selectedItem?.companyName || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('customer.phone')}>{selectedItem?.phone || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('customer.email')}>{selectedItem?.email || '-'}</Descriptions.Item>
                      </Descriptions>
                    </Col>
                    <Col span={12}>
                      <Descriptions bordered column={1}>
                        <Descriptions.Item label={t('customer.street')}>{selectedItem?.address?.street || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('customer.city')}>{selectedItem?.address?.city || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('customer.state')}>{selectedItem?.address?.state || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('customer.postalCode')}>{selectedItem?.address?.postalCode || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('customer.country')}>{selectedItem?.address?.country?.name || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('customer.taxId')}>{selectedItem?.taxId || '-'}</Descriptions.Item>
                      </Descriptions>
                    </Col>
                  </Row>

                  <Divider />

                  <div data-tour="customer-events-table">
                    <DataTable
                    columns={eventColumns}
                    data={customerEvents}
                    expandedRowRender={expandedEventRow}
                    enableFrontendFiltering={false}
                    defaultExpandAllRows={false}
                    defaultPageSize={50}
                    />
                  </div>
                </>
              ),
            },
            ...(currentUser?.role !== UserRole.CUSTOMER ? [{
              key: 'servicePrices',
              label: <span data-tour="customer-tab-service-prices">{t('customer.servicePrices') || 'Service Prices'}</span>,
              children: <div data-tour="customer-service-prices"><CustomerServicePrices customerId={customer?.id || ''} /></div>,
            }] : []),
          ]}
        />
      </Card>
    </div>
  );
};

export default CustomerDetails;
